import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useNavigationType } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { RootContext } from "../context/root-provider";
import { useDropAnything } from "../context/DropAnythingContext";
import { readMeetingDoc, readMeetingTopic } from "../services/zoomMeeting";
import styles from "./ZoomMeetingPage.module.scss";

/**
 * Home inside a Zoom meeting (TT-1207, F1): this meeting, not the library.
 * The meeting's tapestry is what the user came for. Running and set up, a
 * landing opens it; auto-started by Zoom and not yet named, the page offers
 * the naming first (the start modal's running state, TT-1232); closed, it is
 * the tapestry the meeting made, a click away after Stop. With none, the
 * page offers the start. The library stays a menu item away (/library).
 */

// The card for what the meeting has: its words and its one or two actions.
function cardFor(doc, topic, { open, start }) {
  if (!doc) {
    return {
      eyebrow: "This meeting",
      heading: topic,
      note: "No live tapestry is drawing yet.",
      primary: { label: "Start Live Tapestry", icon: <PaletteIcon />, onClick: start },
    };
  }
  const heading = doc.title || "Live Tapestry";
  if (doc.status !== "running") {
    return {
      eyebrow: "This meeting",
      heading,
      note:
        doc.status === "error"
          ? "This meeting's tapestry stopped with an error."
          : "This meeting's tapestry has finished.",
      primary: { label: "Open", icon: <PaletteIcon />, onClick: open },
      secondary: { label: "Start another", onClick: start },
    };
  }
  if (!doc.setUp) {
    // Zoom's auto-start made it through the webhook; the modal is the one
    // place that names it.
    return {
      eyebrow: "Live now",
      heading,
      note: "Zoom started this meeting's capture. Name its tapestry, then open it.",
      primary: { label: "Name it", icon: <EditOutlinedIcon />, onClick: start },
      secondary: { label: "Open", onClick: open },
    };
  }
  return {
    eyebrow: "Live now",
    heading,
    note: "This meeting's tapestry is drawing.",
    primary: { label: "Open", icon: <PaletteIcon />, onClick: open },
  };
}

export default function ZoomMeetingPage() {
  const { userInfo } = useContext(RootContext);
  const { openStartModal } = useDropAnything();
  const navigate = useNavigate();
  // A landing (the page load, a reload) opens the tapestry. A visit the user
  // chose (the live tap's back arrow, the logo) shows it as a card instead
  // of bouncing back in.
  const landed = useNavigationType() === "POP";
  const userId = userInfo?.username;

  const [doc, setDoc] = useState(undefined); // undefined: reading
  const [topic, setTopic] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    (async () => {
      const found = await readMeetingDoc(userId);
      if (!active) return;
      if (landed && found?.status === "running" && found.setUp) {
        navigate(`/live-tap/${found.docId}`, { replace: true });
        return;
      }
      setDoc(found);
    })();
    return () => {
      active = false;
    };
  }, [userId, navigate, landed]);

  useEffect(() => {
    let active = true;
    readMeetingTopic().then((found) => {
      if (active) setTopic(found);
    });
    return () => {
      active = false;
    };
  }, []);

  if (doc === undefined) {
    return (
      <div className={styles.pageContainer}>
        <CircularProgress />
      </div>
    );
  }

  const card = cardFor(doc, topic, {
    open: () => navigate(`/live-tap/${doc.docId}`),
    start: () => openStartModal(),
  });

  return (
    <div className={styles.pageContainer}>
      <section className={styles.card} aria-label={card.eyebrow}>
        <span className={styles.eyebrow}>{card.eyebrow}</span>
        {card.heading && <h1 className={styles.heading}>{card.heading}</h1>}
        <p className={styles.note}>{card.note}</p>
        <div className={styles.actions}>
          <button className={styles.actionBtn} onClick={card.primary.onClick}>
            {card.primary.icon} {card.primary.label}
          </button>
          {card.secondary && (
            <button className={styles.secondaryBtn} onClick={card.secondary.onClick}>
              {card.secondary.label}
            </button>
          )}
        </div>
      </section>
      <p className={styles.library}>
        Looking for an earlier one? <Link to="/library">Your tapestries</Link>
      </p>
    </div>
  );
}
